import { create } from 'zustand';

export interface RecipientData {
  id: string;
  name: string;
  email: string;
  avatarUrl?: string;
  programs: { id: string; name: string }[];
}

interface AffiliateBulkEmailStore {
  selectedIds: Set<string>;
  recipients: Map<string, RecipientData>;
  programId: string | null;
  /** True when the user opted to act on every affiliate matching the filter. */
  selectAllMatching: boolean;
  /** Total affiliates matching the current filter (from the list meta). */
  matchingTotal: number;

  toggleSelection: (id: string) => void;
  toggleSelectAll: (ids: string[]) => void;
  selectSingle: (id: string) => void;
  clearSelection: () => void;
  enableSelectAllMatching: (total: number) => void;
  setMatchingTotal: (total: number) => void;

  addRecipient: (recipient: RecipientData) => void;
  removeRecipient: (id: string) => void;
  removeRecipients: (ids: string[]) => void;
  setProgramId: (id: string | null) => void;
  setRecipients: (list: RecipientData[]) => void;
}

/** Returns recipients that don't belong to the selected program */
export function getIncompatibleRecipients(
  recipients: Map<string, RecipientData>,
  programId: string | null
): RecipientData[] {
  if (!programId) return [];
  return Array.from(recipients.values()).filter(
    (r) => r.programs.length > 0 && !r.programs.some((p) => p.id === programId)
  );
}

const useAffiliateBulkEmailStore = create<AffiliateBulkEmailStore>((set, get) => ({
  selectedIds: new Set<string>(),
  recipients: new Map<string, RecipientData>(),
  programId: null,
  selectAllMatching: false,
  matchingTotal: 0,

  toggleSelection: (id) => {
    const { selectedIds } = get();
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    // A manual change is an explicit-id selection: leave the filter-wide mode.
    set({ selectedIds: next, selectAllMatching: false });
  },

  toggleSelectAll: (ids) => {
    const { selectedIds } = get();
    const allSelected = ids.length > 0 && ids.every((id) => selectedIds.has(id));
    const next = new Set(selectedIds);
    if (allSelected) {
      for (const id of ids) next.delete(id);
    } else {
      for (const id of ids) next.add(id);
    }
    set({ selectedIds: next, selectAllMatching: false });
  },

  selectSingle: (id) => {
    set({ selectedIds: new Set([id]), selectAllMatching: false });
  },

  clearSelection: () => {
    set({
      selectedIds: new Set(),
      recipients: new Map(),
      programId: null,
      selectAllMatching: false,
      matchingTotal: 0,
    });
  },

  enableSelectAllMatching: (total) => {
    set({ selectAllMatching: true, matchingTotal: total });
  },

  setMatchingTotal: (total) => {
    set({ matchingTotal: total });
  },

  addRecipient: (recipient) => {
    const { selectedIds, recipients } = get();
    if (selectedIds.has(recipient.id)) return;
    const nextIds = new Set(selectedIds);
    nextIds.add(recipient.id);
    const nextRecipients = new Map(recipients);
    nextRecipients.set(recipient.id, recipient);
    set({ selectedIds: nextIds, recipients: nextRecipients, selectAllMatching: false });
  },

  removeRecipient: (id) => {
    const { selectedIds, recipients } = get();
    const nextIds = new Set(selectedIds);
    nextIds.delete(id);
    const nextRecipients = new Map(recipients);
    nextRecipients.delete(id);
    set({ selectedIds: nextIds, recipients: nextRecipients, selectAllMatching: false });
  },

  removeRecipients: (ids) => {
    const { selectedIds, recipients } = get();
    const nextIds = new Set(selectedIds);
    const nextRecipients = new Map(recipients);
    for (const id of ids) {
      nextIds.delete(id);
      nextRecipients.delete(id);
    }
    set({ selectedIds: nextIds, recipients: nextRecipients, selectAllMatching: false });
  },

  setProgramId: (id) => set({ programId: id }),

  setRecipients: (list) => {
    const nextIds = new Set<string>();
    const nextRecipients = new Map<string, RecipientData>();
    for (const r of list) {
      nextIds.add(r.id);
      nextRecipients.set(r.id, r);
    }
    set({ selectedIds: nextIds, recipients: nextRecipients, selectAllMatching: false });
  },
}));

export default useAffiliateBulkEmailStore;
